import React, { useEffect, useState } from "react";
import {
  Card,
  Image,
  Typography,
  Spin,
  Button,
  message,
  Modal,
} from "antd";
import { useMediaQuery } from "react-responsive";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const CartOverView = ({ refreshCartCount }) => {
  const navigate = useNavigate();
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderModal, setOrderModal] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [messageApi] = message.useMessage(); // Use AntD message API

  useEffect(() => {
    checkUserActiveStatus();
    fetchCartData();
    // eslint-disable-next-line
  }, []);

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          // Redirect to home if AllowtoOrder is false
          if (response.data.AllowtoOrder === false) {
            window.location.href = "/";
            return;
          }

          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === true
          ) {
            alert("Your session has expired. Please log in again.");

            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchCartData = async () => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        global.config.APIURL.BaseURL + "api/UserCart/GetCartData",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setCartData(response.data || []);
    } catch (error) {
      setCartData([]);
    }
    setLoading(false);
  };

  // Totals
  const totalBox = cartData.reduce((sum, item) => sum + (item.NoOfBox || 0), 0);
  const totalAmount = cartData.reduce(
    (sum, item) => sum + (item.Amount || 0),
    0
  );
  const totalItems = new Set(cartData.map((item) => item.DesignNo)).size;

  // Calculate GST and Grand Total
  const gstPercent = 3;
  const gstAmount = (totalAmount * gstPercent) / 100;
  const grandTotal = totalAmount + gstAmount;

  const handleConfirmOrder = async () => {
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/Order/InsertOrder",
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );

      if (response.status === 200) {
        refreshCartCount();

        setOrderModal(true); // Show modal only on success
      } else {
        messageApi.open({
          type: "error",
          content: "Failed to place order. Please try again.",
        });
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Failed to place order. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <Card style={{ padding: 0, margin: 0 }}>
        <div
          style={{
            margin: isMobile ? "0" : "auto",
            padding: isMobile ? 8 : 24,
            minHeight: "100vh",
          }}
        >
          <Title
            level={2}
            style={{
              fontSize: isMobile ? 22 : 28,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            Confirm Order Items
          </Title>
          <Card
            style={{
              border: "1px solid #3492eb",
              padding: 0,
            }}
          >
            {cartData.length === 0 ? (
              <div style={{ textAlign: "center", color: "#888" }}>
                No products in cart.
              </div>
            ) : (
              cartData.map((item, idx) => (
                <div key={item.ID || idx}>
                  {isMobile ? (
                    <>
                      {/* MOBILE VIEW */}
                      {/* First row: image + product name, design no, color/size */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          width: "100%",
                        }}
                      >
                        <Image
                          src={item.ImageUrl}
                          alt={item.ProductName}
                          width={60}
                          height={60}
                          style={{
                            objectFit: "cover",
                            borderRadius: 8,
                            margin: 0,
                            minWidth: 60,
                          }}
                          fallback="https://via.placeholder.com/100"
                        />
                        <div style={{ flex: 1 }}>
                          <Title
                            level={5}
                            style={{
                              margin: 0,
                              fontSize: 14,
                              lineHeight: 1.2,
                              fontWeight: 700,
                            }}
                          >
                            {item.ProductName}
                          </Title>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              lineHeight: 1.2,
                              fontWeight: 400,
                              display: "block",
                              marginTop: 2,
                            }}
                          >
                            Design No: {item.DesignNo}
                          </Text>
                          <Text
                            style={{
                              fontSize: 12,
                              lineHeight: 1.2,
                              display: "block",
                              fontWeight: 400,
                              marginTop: 2,
                            }}
                          >
                            Color:{" "}
                            {item.Color && item.Color.trim()
                              ? item.Color
                              : "Mix Color"}{" "}
                            | Size: {item.Size}
                          </Text>
                        </div>
                      </div>
                      {/* Second row: 2 columns */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 16,
                          marginTop: 8,
                        }}
                      >
                        {/* Left column */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text style={{ fontSize: 12, display: "block" }}>
                            <b>Box:</b> {item.NoOfBox}
                          </Text>
                          <Text style={{ fontSize: 12, display: "block" }}>
                            <b>Box Size:</b> {item.BoxSize}
                          </Text>
                          <Text style={{ fontSize: 12, display: "block" }}>
                            <b>Total Qty:</b> {item.TotalQuantity}
                          </Text>
                        </div>
                        {/* Right column */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text style={{ fontSize: 12, display: "block" }}>
                            <b>Price:</b> ₹{item.Price.toFixed(2)}
                          </Text>
                          <Text style={{ fontSize: 13, display: "block" }}>
                            <b>Total:</b> ₹{item.Amount.toFixed(2)}
                          </Text>
                          {item.Detail && (
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 12,
                                display: "block",
                                marginTop: 2,
                              }}
                            >
                              {item.Detail}
                            </Text>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* DESKTOP VIEW (unchanged) */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 16,
                          padding: "12px 0",
                        }}
                      >
                        {/* First column: Product info */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 16,
                            width: 360,
                            minWidth: 320,
                          }}
                        >
                          <Image
                            src={item.ImageUrl}
                            alt={item.ProductName}
                            width={90}
                            height={90}
                            style={{
                              objectFit: "cover",
                              borderRadius: 8,
                              margin: 0,
                              minWidth: 90,
                            }}
                            fallback="https://via.placeholder.com/100"
                          />
                          <div style={{ flex: 1 }}>
                            <Title
                              level={5}
                              style={{
                                margin: 0,
                                fontSize: 18,
                                lineHeight: 1.2,
                                fontWeight: 700,
                              }}
                            >
                              {item.ProductName}
                            </Title>
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 14,
                                lineHeight: 1.2,
                                fontWeight: 400,
                                display: "block",
                              }}
                            >
                              Design No: {item.DesignNo}
                            </Text>
                            <Text
                              style={{
                                fontSize: 14,
                                lineHeight: 1.2,
                                display: "block",
                                fontWeight: 400,
                              }}
                            >
                              Color:{" "}
                              {item.Color && item.Color.trim()
                                ? item.Color
                                : "Mix Color"}{" "}
                              | Size: {item.Size}
                            </Text>
                          </div>
                        </div>
                        {/* Middle column: Box, Box Size, Total Qty */}
                        <div
                          style={{
                            textAlign: "left",
                            minWidth: 90,
                            marginBottom: 0,
                            marginLeft: 16,
                          }}
                        >
                          <Text style={{ fontSize: 14, display: "block" }}>
                            <b>Box:</b> {item.NoOfBox}
                          </Text>
                          <Text style={{ fontSize: 14, display: "block" }}>
                            <b>Box Size:</b> {item.BoxSize}
                          </Text>
                          <Text style={{ fontSize: 14, display: "block" }}>
                            <b>Total Qty:</b> {item.TotalQuantity}
                          </Text>
                        </div>
                        {/* Last column: Price, Total, Detail */}
                        <div
                          style={{
                            textAlign: "left",
                            minWidth: 90,
                            marginBottom: 0,
                            marginLeft: 16,
                          }}
                        >
                          <Text style={{ fontSize: 14, display: "block" }}>
                            <b>Price:</b> ₹{item.Price.toFixed(2)}
                          </Text>
                          <Text style={{ fontSize: 15, display: "block" }}>
                            <b>Total:</b> ₹{item.Amount.toFixed(2)}
                          </Text>
                          {item.Detail && (
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 14,
                                display: "block",
                                marginTop: 2,
                              }}
                            >
                              {item.Detail}
                            </Text>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {idx !== cartData.length - 1 && (
                    <div
                      style={{
                        borderBottom: "1px solid #eee",
                        margin: isMobile ? "4px 0" : "8px 0",
                        width: "100%",
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </Card>
          {/* Totals at the bottom */}
          <div
            style={{
              marginTop: isMobile ? 16 : 32,
              padding: isMobile ? 8 : 16,
              background: "#fafafa",
              borderRadius: 8,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: isMobile ? "flex-start" : "flex-end",
              gap: isMobile ? 8 : 40,
              fontSize: isMobile ? 15 : 18,
              fontWeight: 500,
              position: isMobile ? "sticky" : "static",
              bottom: isMobile ? 0 : "unset",
              left: 0,
              right: 0,
              zIndex: 100,
            }}
          >
            <span>
              Total Items: <b>{totalItems}</b>
            </span>
            <span>
              Total Box: <b>{totalBox}</b>
            </span>
            <span>
              Total Amount: <b>₹{totalAmount.toFixed(2)}</b>
            </span>
            <span>
              GST (3%): <b>₹{gstAmount.toFixed(2)}</b>
            </span>
            <span>
              Grand Total:{" "}
              <b style={{ color: "#1890ff" }}>₹{grandTotal.toFixed(2)}</b>
            </span>
            <Button
              type="primary"
              size={isMobile ? "middle" : "large"}
              style={{
                marginTop: isMobile ? 8 : 0,
                marginLeft: isMobile ? 0 : 24,
                width: isMobile ? "100%" : "auto",
                alignSelf: isMobile ? "stretch" : "center",
              }}
              onClick={handleConfirmOrder}
            >
              Confirm Order
            </Button>
          </div>
          <Modal
            open={orderModal}
            footer={[
              <Button
                key="ok"
                type="primary"
                onClick={() => {
                  setOrderModal(false);
                  navigate("/productcategories");
                }}
              >
                OK
              </Button>,
            ]}
            closable={false}
            centered
          >
            <div style={{ textAlign: "center", fontSize: 18, margin: 16 }}>
              Order placed successfully
              <br />
              <span style={{ color: "#52c41a", fontWeight: 600, fontSize: 20 }}>
                Thank you for your order!
              </span>
            </div>
          </Modal>
        </div>
      </Card>
    </div>
  );
};

export default CartOverView;
